import { clearCoveredPending, sourceStatus } from './membership';
import { classifyRows, covers, matches, mergeRows, reopenInitial, sameSymbols } from './rows';
export class ResponseDefect extends Error {
    name = 'ResponseDefect';
}
export function commitResponse(state, result, now, adapter) {
    const instruction = state.fetch.instruction;
    const fail = (message) => ({
        ...state,
        fetch: { status: 'failed', instruction, error: new ResponseDefect(message) },
    });
    if (result.kind !== instruction.kind)
        return fail('Result kind does not match the issued instruction');
    for (const row of result.rows) {
        if (!adapter.keyOf(row) || adapter.symbolsOf(row).length === 0) {
            return fail('A response row lacks identity or symbols');
        }
    }
    const terminal = result.kind === 'initial'
        ? result.caughtUp && result.reachedEnd
        : result.kind === 'up'
            ? result.caughtUp
            : result.kind === 'down'
                ? result.reachedEnd
                : result.complete;
    const tail = result.rows[result.rows.length - 1];
    if (result.kind !== 'initial' && !terminal && tail === undefined)
        return fail('Nonterminal page has no raw tail');
    // Endpoints return rows outward from the exclusive position; pass traversal is newest to oldest.
    const reference = instruction.kind === 'up'
        ? state.rows[0]
        : instruction.kind === 'down'
            ? state.rows[state.rows.length - 1]
            : instruction.kind === 'backfill' && state.backfill.status === 'paging'
                ? state.backfill.tail
                : instruction.kind === 'lead' && state.lead.refresh.status === 'paging'
                    ? state.lead.refresh.tail
                    : null;
    if (result.kind !== 'initial' && !terminal && reference !== null && reference !== undefined && tail !== undefined) {
        const difference = adapter.compare(tail, reference);
        if (instruction.kind === 'up' ? difference >= 0 : difference <= 0) {
            return fail('Nonterminal page did not advance beyond its exclusive position');
        }
    }
    let admitted = result.rows.filter((row) => matches(row, instruction.symbols, adapter));
    if (instruction.kind === 'backfill') {
        admitted =
            state.rows.length === 0
                ? []
                : admitted.filter((row) => adapter.compare(row, state.rows[0]) >= 0 &&
                    adapter.compare(row, state.rows[state.rows.length - 1]) <= 0);
    }
    const lead = instruction.kind === 'lead';
    let next = { ...state, fetch: { status: 'idle' } };
    if (instruction.kind === 'down' || (instruction.kind === 'up' && instruction.cause !== 'live')) {
        const oldKeys = new Set(state.rows.map(adapter.keyOf));
        const pagedRowKeys = new Set(state.pagedRowKeys);
        for (const row of admitted) {
            const key = adapter.keyOf(row);
            if (!oldKeys.has(key))
                pagedRowKeys.add(key);
        }
        next = { ...next, pagedRowKeys };
    }
    if (lead && next.lead.refresh.status === 'paging') {
        next = {
            ...next,
            lead: {
                ...next.lead,
                refresh: { ...next.lead.refresh, staging: mergeRows(next.lead.refresh.staging, admitted, adapter) },
            },
        };
    }
    else
        next = { ...next, rows: mergeRows(next.rows, admitted, adapter) };
    next = classifyRows(next, now, false, adapter);
    const admittedKeys = new Set(admitted.map(adapter.keyOf));
    const newlyPresent = next.rows.filter((row) => admittedKeys.has(adapter.keyOf(row)) &&
        !state.rows.some((old) => adapter.keyOf(old) === adapter.keyOf(row)));
    const anyAdmitted = next.rows.some((row) => admittedKeys.has(adapter.keyOf(row)));
    const covered = covers(instruction.symbols, next.roundSymbols);
    const unchanged = sameSymbols(instruction.symbols, next.roundSymbols);
    switch (result.kind) {
        case 'initial': {
            next = clearCoveredPending({
                ...next,
                initial: 'committed',
                caughtUp: covered && result.caughtUp,
                reachedEnd: covered && result.reachedEnd,
            }, instruction.symbols);
            if (next.rows.length === 0) {
                if (!covered)
                    next = reopenInitial(next);
                else if (result.caughtUp && result.reachedEnd) {
                    /* authoritative empty exhaustion */
                }
                else if (!unchanged)
                    next = reopenInitial(next);
                else {
                    next = {
                        ...next,
                        fetch: {
                            status: 'failed',
                            instruction,
                            error: new ResponseDefect('Nonterminal initial response left an empty window'),
                        },
                    };
                }
            }
            break;
        }
        case 'up':
        case 'down': {
            if (!terminal && newlyPresent.length === 0 && unchanged) {
                return fail('Nonterminal directional page admitted no new rows under unchanged membership');
            }
            if (result.kind === 'up') {
                next = { ...next, caughtUp: covered && result.caughtUp };
                if (instruction.kind === 'up' && instruction.cause === 'live') {
                    next = { ...next, liveCatchUp: !result.caughtUp && anyAdmitted ? 'active' : 'inactive' };
                }
            }
            else
                next = { ...next, reachedEnd: covered && result.reachedEnd };
            break;
        }
        case 'backfill':
            if (next.backfill.status !== 'paging')
                return fail('Backfill response has no frozen pass');
            {
                const pass = next.backfill;
                if (result.complete) {
                    next = {
                        ...next,
                        backfill: { status: 'none' },
                        pending: next.pending.filter((entry) => !pass.entries.some((recorded) => recorded.symbol === entry.symbol && recorded.token === entry.token)),
                    };
                }
                else {
                    next = {
                        ...next,
                        backfill: { ...pass, page: { kind: 'after', position: adapter.cursorOf(tail) }, tail: tail },
                    };
                }
            }
            break;
        case 'lead':
            if (next.lead.refresh.status !== 'paging')
                return fail('Lead response has no frozen pass');
            {
                const pass = next.lead.refresh;
                if (result.complete) {
                    next = {
                        ...next,
                        lead: { rows: pass.staging, refresh: { status: pass.refreshQueued ? 'due' : 'current' } },
                    };
                }
                else {
                    next = {
                        ...next,
                        lead: {
                            ...next.lead,
                            refresh: {
                                ...pass,
                                page: { kind: 'after', position: adapter.cursorOf(tail) },
                                tail: tail,
                            },
                        },
                    };
                }
                next = classifyRows(next, now, false, adapter);
            }
            break;
    }
    return sourceStatus(next.caughtUp ? { ...next, liveTopRecovery: false } : next);
}
//# sourceMappingURL=response.js.map